import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import api from '../lib/api';
import { formatCurrency, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { 
  Wallet, 
  Save,
  Loader2,
  Euro,
  Calendar,
  TrendingUp,
  FileText,
  ExternalLink,
  Filter
} from 'lucide-react';

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

const Commissions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [commissions, setCommissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  
  // Overview state
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [availableYears, setAvailableYears] = useState([]);

  const loadData = useCallback(async () => {
    try {
      const [productsRes, commissionsRes] = await Promise.all([
        api.getProducts(),
        api.getCommissions()
      ]);
      
      setProducts(productsRes.data);
      
      const commMap = {};
      commissionsRes.data.forEach(c => {
        commMap[c.product_id] = c.amount;
      });
      setCommissions(commMap);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const params = {};
      if (selectedYear !== 'all') {
        params.year = parseInt(selectedYear);
      }
      if (selectedMonth !== 'all') {
        params.month = parseInt(selectedMonth);
      }
      
      const response = await api.getCommissionsOverview(params);
      setOverview(response.data);
      
      // Extract available years from months
      const years = new Set();
      response.data.months_available.forEach(m => {
        const year = m.split('-')[0];
        years.add(year);
      });
      setAvailableYears(Array.from(years).sort((a, b) => b - a));
    } catch (error) {
      toast.error('Fehler beim Laden der Provisionsübersicht');
    } finally {
      setOverviewLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const handleSave = async (productId) => {
    const amount = parseFloat(commissions[productId]) || 0;
    setSaving({ ...saving, [productId]: true });

    try {
      await api.setCommission({ product_id: productId, amount });
      toast.success('Provision gespeichert');
      // Reload overview after saving
      loadOverview();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving({ ...saving, [productId]: false });
    }
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F47920]"></div>
      </div>
    );
  }

  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Provisionsverwaltung
        </h1>
        <p className="text-slate-600 mt-1">
          {isAdmin 
            ? 'Verwalten Sie Provisionen und sehen Sie alle Auszahlungen'
            : 'Übersicht Ihrer verdienten Provisionen'
          }
        </p>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="overview" className="gap-2" data-testid="tab-overview">
            <TrendingUp className="w-4 h-4" />
            Übersicht
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="settings" className="gap-2" data-testid="tab-settings">
              <Euro className="w-4 h-4" />
              Einstellungen
            </TabsTrigger>
          )}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Summary Card */}
          <Card className="border-0 shadow-sm bg-gradient-to-r from-emerald-50 to-teal-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-emerald-700">
                    {selectedYear === 'all' && selectedMonth === 'all' 
                      ? 'Gesamte Provision'
                      : selectedMonth !== 'all' 
                        ? `Provision ${MONTH_NAMES[parseInt(selectedMonth) - 1]} ${selectedYear}`
                        : `Provision ${selectedYear}`
                    }
                  </p>
                  <p className="text-4xl font-bold text-emerald-800 mt-1" data-testid="total-commission">
                    {overviewLoading ? '...' : formatCurrency(overview?.total_commission || 0)}
                  </p>
                </div>
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Wallet className="w-8 h-8 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Filters */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <Filter className="w-4 h-4 text-[#F47920]" />
                Filter
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <div className="w-48">
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Jahr</label>
                  <Select value={selectedYear} onValueChange={setSelectedYear}>
                    <SelectTrigger data-testid="year-filter">
                      <SelectValue placeholder="Jahr wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Jahre</SelectItem>
                      {availableYears.map(year => (
                        <SelectItem key={year} value={year}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-48">
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Monat</label>
                  <Select value={selectedMonth} onValueChange={setSelectedMonth} disabled={selectedYear === 'all'}>
                    <SelectTrigger data-testid="month-filter">
                      <SelectValue placeholder="Monat wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Monate</SelectItem>
                      {MONTH_NAMES.map((name, idx) => (
                        <SelectItem key={idx + 1} value={String(idx + 1)}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Commission Details Table */}
          <Card className="border-0 shadow-sm overflow-hidden">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#F47920]" />
                Provisionsdetails
              </CardTitle>
              <CardDescription>
                {isAdmin 
                  ? 'Alle bestätigten Bestellungen mit Provisionszuordnung'
                  : 'Ihre bestätigten Bestellungen mit Provisionen'
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {overviewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#F47920]" />
                </div>
              ) : overview?.commission_details?.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Wallet className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Keine bestätigten Bestellungen im gewählten Zeitraum</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-semibold">Bestellnr.</TableHead>
                        <TableHead className="font-semibold">Kunde</TableHead>
                        <TableHead className="font-semibold">Produkt</TableHead>
                        {isAdmin && <TableHead className="font-semibold">Vertriebler</TableHead>}
                        <TableHead className="font-semibold">Bestätigt am</TableHead>
                        <TableHead className="font-semibold text-right">Provision</TableHead>
                        <TableHead className="w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {overview?.commission_details?.map((item, idx) => (
                        <TableRow key={idx} data-testid={`commission-detail-${item.order_number}`}>
                          <TableCell>
                            <Badge variant="outline" className="font-mono">
                              #{item.order_number}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">{item.customer_name}</TableCell>
                          <TableCell>
                            <span className="text-slate-600">{item.product_name}</span>
                          </TableCell>
                          {isAdmin && (
                            <TableCell>
                              <span className="text-slate-600">{item.sales_user_name}</span>
                            </TableCell>
                          )}
                          <TableCell>
                            <div className="flex items-center gap-1.5 text-slate-600">
                              <Calendar className="w-4 h-4" />
                              {formatDateTime(item.confirmed_at)}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="font-semibold text-emerald-600">
                              {formatCurrency(item.commission_amount)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/orders/${item.order_id}`)}
                              className="text-slate-400 hover:text-[#F47920]"
                              data-testid={`view-order-${item.order_number}`}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              {/* Monthly Summary */}
              {overview?.months_available?.length > 0 && selectedYear === 'all' && (
                <div className="mt-6 pt-6 border-t">
                  <h4 className="font-medium text-slate-700 mb-3">Verfügbare Zeiträume</h4>
                  <div className="flex flex-wrap gap-2">
                    {overview.months_available.map(monthStr => (
                      <Badge 
                        key={monthStr}
                        variant="secondary"
                        className="cursor-pointer hover:bg-slate-200"
                        onClick={() => {
                          const [year, month] = monthStr.split('-');
                          setSelectedYear(year);
                          setSelectedMonth(String(parseInt(month)));
                        }}
                      >
                        {getMonthName(monthStr)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="settings" className="space-y-6 mt-6">
            {/* Info Card */}
            <Card className="border-0 shadow-sm bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Wallet className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-amber-900">Provisionsberechnung</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Die Provision wird dem Vertriebsmitarbeiter gutgeschrieben, sobald eine Bestellung 
                      vom Kunden bestätigt wurde. Die Summe wird im Dashboard des jeweiligen Mitarbeiters angezeigt.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Commissions Table */}
            <Card className="border-0 shadow-sm overflow-hidden">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Euro className="w-5 h-5 text-[#F47920]" />
                  Provisionen pro Produkt
                </CardTitle>
                <CardDescription>
                  Fester Betrag in Euro pro bestätigter Bestellung
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Produkt</TableHead>
                      <TableHead className="font-semibold">Monatspreis</TableHead>
                      <TableHead className="font-semibold w-48">Provision</TableHead>
                      <TableHead className="w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.id} data-testid={`commission-row-${product.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{product.name}</p>
                            <p className="text-sm text-slate-500">Art.-Nr.: {product.article_number}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(product.monthly_price)}
                        </TableCell>
                        <TableCell>
                          <div className="relative">
                            <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={commissions[product.id] || ''}
                              onChange={(e) => setCommissions({
                                ...commissions,
                                [product.id]: e.target.value
                              })}
                              className="pl-10 w-full"
                              placeholder="0.00"
                              data-testid={`commission-input-${product.id}`}
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            onClick={() => handleSave(product.id)}
                            disabled={saving[product.id]}
                            size="sm"
                            className="bg-[#F47920] hover:bg-[#E06810]"
                            data-testid={`save-commission-${product.id}`}
                          >
                            {saving[product.id] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Save className="w-4 h-4" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default Commissions;
